import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { showcaseAPI } from '../../services/api';
import { ShowcaseSection, ShowcasePage, ShowcaseItem } from '../../types';
import toast from 'react-hot-toast';

interface ShowcaseState {
  sections: ShowcaseSection[];
  loading: boolean;
  error: string | null;
}

const initialState: ShowcaseState = {
  sections: [],
  loading: false,
  error: null,
};

// Mock data for initial development
const MOCK_SECTIONS: ShowcaseSection[] = [
  {
    id: '1',
    user_id: 'user1',
    title: 'Wedding',
    order: 0,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    pages: [
      {
        id: 'p1',
        title: 'Ceremony Highlights',
        layout_type: 'grid',
        order: 0,
        items: [
          {
            id: 'i1',
            image_url: 'https://images.unsplash.com/photo-1519741497674-611481863552',
            x: 0, y: 0, w: 6, h: 6,
            border_type: 'solid',
            order: 0
          },
          {
            id: 'i2',
            image_url: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622',
            x: 6, y: 0, w: 6, h: 3,
            border_type: 'fading-soft',
            order: 1
          }
        ]
      },
      {
        id: 'p2',
        title: 'Artistic Collage',
        layout_type: 'canvas',
        order: 1,
        items: [
          {
            id: 'i3',
            image_url: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed',
            x: 50, y: 50, w: 300, h: 200,
            rotation: -5,
            border_type: 'fading-blended',
            order: 0
          },
          {
            id: 'i4',
            image_url: 'https://images.unsplash.com/photo-1465495910483-0d6749ee98ce',
            x: 200, y: 150, w: 250, h: 300,
            rotation: 10,
            border_type: 'fading-soft',
            order: 1
          }
        ]
      }
    ]
  }
];

export const fetchUserShowcase = createAsyncThunk(
  'showcase/fetchUserShowcase',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await showcaseAPI.getUserShowcase(userId);
      return response.data.data || [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch showcase');
    }
  }
);

export const createSectionAsync = createAsyncThunk(
  'showcase/createSection',
  async (data: { title: string }, { rejectWithValue }) => {
    try {
      const response = await showcaseAPI.createSection(data);
      // After creating section, create a default page
      const section = response.data.data;
      const pageResponse = await showcaseAPI.createPage({
        section_id: section.id,
        title: 'Main Layout',
        layout_type: 'grid'
      });
      section.pages = [pageResponse.data.data];
      return section;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create section');
    }
  }
);

export const createPageAsync = createAsyncThunk(
  'showcase/createPage',
  async (data: { sectionId: string; title: string; layoutType: 'grid' | 'canvas' }, { rejectWithValue }) => {
    try {
      const response = await showcaseAPI.createPage({
        section_id: data.sectionId,
        title: data.title,
        layout_type: data.layoutType
      });
      return { sectionId: data.sectionId, page: response.data.data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create page');
    }
  }
);

export const createItemAsync = createAsyncThunk(
  'showcase/createItem',
  async (data: { sectionId: string; pageId: string; item: Omit<ShowcaseItem, 'id'> }, { rejectWithValue }) => {
    try {
      const response = await showcaseAPI.createItem({
        page_id: data.pageId,
        image_url: data.item.image_url,
        thumbnail_url: data.item.thumbnail_url,
        x: data.item.x,
        y: data.item.y,
        w: data.item.w,
        h: data.item.h,
        rotation: data.item.rotation,
        intensity: data.item.intensity,
        border_radius: data.item.border_radius,
        border_type: data.item.border_type,
        direction: data.item.direction,
        order: data.item.order
      });
      return { sectionId: data.sectionId, pageId: data.pageId, item: response.data.data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create item');
    }
  }
);

export const updateSectionAsync = createAsyncThunk(
  'showcase/updateSection',
  async (section: ShowcaseSection, { rejectWithValue }) => {
    try {
      await showcaseAPI.updateSection(section.id, {
        title: section.title,
        order: section.order
      });
      return section;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update section');
    }
  }
);

export const updatePageAsync = createAsyncThunk(
  'showcase/updatePage',
  async (data: { sectionId: string; page: ShowcasePage }, { rejectWithValue }) => {
    try {
      await showcaseAPI.updatePage(data.page.id, {
        title: data.page.title,
        layout_type: data.page.layout_type,
        order: data.page.order
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update page');
    }
  }
);

export const updateItemAsync = createAsyncThunk(
  'showcase/updateItem',
  async (data: { sectionId: string; pageId: string; item: ShowcaseItem }, { rejectWithValue }) => {
    try {
      await showcaseAPI.updateItem(data.item.id, {
        image_url: data.item.image_url,
        thumbnail_url: data.item.thumbnail_url,
        x: data.item.x,
        y: data.item.y,
        w: data.item.w,
        h: data.item.h,
        rotation: data.item.rotation,
        intensity: data.item.intensity,
        border_radius: data.item.border_radius,
        border_type: data.item.border_type,
        direction: data.item.direction,
        order: data.item.order
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update item');
    }
  }
);

export const deleteSectionAsync = createAsyncThunk(
  'showcase/deleteSection',
  async (sectionId: string, { rejectWithValue }) => {
    try {
      await showcaseAPI.deleteSection(sectionId);
      return sectionId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete section');
    }
  }
);

export const deletePageAsync = createAsyncThunk(
  'showcase/deletePage',
  async (data: { sectionId: string; pageId: string }, { rejectWithValue }) => {
    try {
      await showcaseAPI.deletePage(data.pageId);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete page');
    }
  }
);

const showcaseSlice = createSlice({
  name: 'showcase',
  initialState,
  reducers: {
    addSection: (state, action: PayloadAction<ShowcaseSection>) => {
      state.sections.push(action.payload);
    },
    updateSection: (state, action: PayloadAction<ShowcaseSection>) => {
      const index = state.sections.findIndex(s => s.id === action.payload.id);
      if (index !== -1) {
        state.sections[index] = action.payload;
      }
    },
    deleteSection: (state, action: PayloadAction<string>) => {
      state.sections = state.sections.filter(s => s.id !== action.payload);
    },
    updateItem: (state, action: PayloadAction<{ sectionId: string, pageId: string, item: ShowcaseItem }>) => {
      const { sectionId, pageId, item } = action.payload;
      const section = state.sections.find(s => s.id === sectionId);
      if (section) {
        const page = section.pages.find(p => p.id === pageId);
        if (page) {
          const itemIndex = page.items.findIndex(i => i.id === item.id);
          if (itemIndex !== -1) {
            page.items[itemIndex] = item;
          }
        }
      }
    },
    removeItem: (state, action: PayloadAction<{ sectionId: string, pageId: string, itemId: string }>) => {
      const { sectionId, pageId, itemId } = action.payload;
      const section = state.sections.find(s => s.id === sectionId);
      if (section) {
        const page = section.pages.find(p => p.id === pageId);
        if (page) {
          page.items = page.items.filter(i => i.id !== itemId);
        }
      }
    },
    addItem: (state, action: PayloadAction<{ sectionId: string, pageId: string, item: ShowcaseItem }>) => {
      const { sectionId, pageId, item } = action.payload;
      const section = state.sections.find(s => s.id === sectionId);
      if (section) {
        const page = section.pages.find(p => p.id === pageId);
        if (page) {
          page.items.push(item);
        }
      }
    },
    updatePage: (state, action: PayloadAction<{ sectionId: string, page: Partial<ShowcasePage> & { id: string } }>) => {
      const { sectionId, page } = action.payload;
      const section = state.sections.find(s => s.id === sectionId);
      if (section) {
        const pageIndex = section.pages.findIndex(p => p.id === page.id);
        if (pageIndex !== -1) {
          section.pages[pageIndex] = { ...section.pages[pageIndex], ...page };
        }
      }
    },
    addPage: (state, action: PayloadAction<{ sectionId: string, page: ShowcasePage }>) => {
      const { sectionId, page } = action.payload;
      const section = state.sections.find(s => s.id === sectionId);
      if (section) {
        section.pages.push(page);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserShowcase.pending, (state) => {
        state.loading = true;
        state.sections = [];
      })
      .addCase(fetchUserShowcase.fulfilled, (state, action) => {
        state.loading = false;
        state.sections = action.payload;
      })
      .addCase(fetchUserShowcase.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createSectionAsync.fulfilled, (state, action) => {
        state.sections.push(action.payload);
        toast.success('Section created successfully');
      })
      .addCase(createSectionAsync.rejected, (state, action) => {
        toast.error(action.payload as string || 'Failed to create section');
      })
      .addCase(createPageAsync.fulfilled, (state, action) => {
        const section = state.sections.find(s => s.id === action.payload.sectionId);
        if (section) {
          section.pages.push(action.payload.page);
          toast.success('Page created successfully');
        }
      })
      .addCase(createPageAsync.rejected, (state, action) => {
        toast.error(action.payload as string || 'Failed to create page');
      })
      .addCase(createItemAsync.fulfilled, (state, action) => {
        const section = state.sections.find(s => s.id === action.payload.sectionId);
        if (section) {
          const page = section.pages.find(p => p.id === action.payload.pageId);
          if (page) {
            page.items.push(action.payload.item);
          }
        }
      })
      .addCase(createItemAsync.rejected, (state, action) => {
        toast.error(action.payload as string || 'Failed to save item');
      })
      .addCase(updateSectionAsync.fulfilled, (state, action) => {
        const index = state.sections.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.sections[index] = action.payload;
        }
      })
      .addCase(updatePageAsync.fulfilled, (state, action) => {
        const section = state.sections.find(s => s.id === action.payload.sectionId);
        if (section) {
          const pageIndex = section.pages.findIndex(p => p.id === action.payload.page.id);
          if (pageIndex !== -1) {
            section.pages[pageIndex] = action.payload.page;
          }
        }
      })
      .addCase(updateItemAsync.fulfilled, (state, action) => {
        const section = state.sections.find(s => s.id === action.payload.sectionId);
        if (section) {
          const page = section.pages.find(p => p.id === action.payload.pageId);
          if (page) {
            const itemIndex = page.items.findIndex(i => i.id === action.payload.item.id);
            if (itemIndex !== -1) {
              page.items[itemIndex] = action.payload.item;
            }
          }
        }
      })
      .addCase(deleteSectionAsync.fulfilled, (state, action) => {
        state.sections = state.sections.filter(s => s.id !== action.payload);
        toast.success('Section deleted successfully');
      })
      .addCase(deleteSectionAsync.rejected, (state, action) => {
        toast.error(action.payload as string || 'Failed to delete section');
      })
      .addCase(deletePageAsync.fulfilled, (state, action) => {
        const section = state.sections.find(s => s.id === action.payload.sectionId);
        if (section) {
          section.pages = section.pages.filter(p => p.id !== action.payload.pageId);
          toast.success('Page deleted successfully');
        }
      })
      .addCase(deletePageAsync.rejected, (state, action) => {
        toast.error(action.payload as string || 'Failed to delete page');
      });
  },
});

export const { addSection, updateSection, deleteSection, updateItem, removeItem, addItem, updatePage, addPage } = showcaseSlice.actions;
export default showcaseSlice.reducer;
